import { getReadableDateRange } from 'app-components/utils/date';
import type RootState from 'app-components/utils/redux-store/rootState';
import _groupBy from 'lodash/groupBy';
import { createSelector } from 'reselect';
import { accountsUtils } from '../accounts';
import reportsAdapter from './generatedReportsEntityAdapter';
import { GENERATED_REPORTS_REDUCER_KEY } from './generatedReportsTypes';

export const getStore = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>) => state[GENERATED_REPORTS_REDUCER_KEY];
const reportsSelectors = reportsAdapter.getSelectors(getStore);

export const getEntity = (state: Pick<RootState, GENERATED_REPORTS_REDUCER_KEY>, { location }: { location: string }) =>
  reportsSelectors.selectById(state, location);

export const getIsGenerating = createSelector(getEntity, (entity) => entity?.isGenerating);
export const getReport = createSelector(getEntity, (entity) => entity?.report);
export const getError = createSelector(getEntity, (entity) => entity?.error);

export const getGraphDataFromReport = createSelector(
  getReport,
  (_state: unknown, props: { timeStep?: string }) => props.timeStep,
  (report, timeStep = 'MONTHS') => {
    const rowsByParentId = _groupBy(report?.rows, ({ parentId }) => parentId ?? '-1');
    const graphRows = rowsByParentId['-1'] ?? [];
    const cols = report?.columns ?? [];
    if (graphRows) {
      const income = graphRows?.find((row) => row?.entityId === 'INCOME');
      const spending = graphRows?.find((row) => row?.entityId === 'EXPENSE');
      const netIncome = graphRows?.find((row) => row?.entityId === 'NET_INCOME');

      // the last column that qcs returns is the total column
      // if the date range is less than the interval we just display the total column as the data
      const graphData =
        graphRows[0]?.cellData?.length > 1 ? graphRows[0]?.cellData?.slice(0, -1) : graphRows[0]?.cellData;
      return graphData?.map((_data, index) => {
        const column = cols[index];
        const totals = {
          incomeTotal: income ? Number(income?.cellData?.[index].value) : 0,
          spendingTotal: spending ? -1 * Number(spending?.cellData?.[index].value) : 0,
          netIncomeTotal: netIncome ? Number(netIncome?.cellData?.[index].value) : 0,
          timeStep,
        };
        if (column?.headerType === 'QCS_ENTITY') {
          return {
            category:
              column.headerValue.qcs_entity === 'ACCOUNT'
                ? accountsUtils.getAccountString(column.headerValue.id) || 'Unknown'
                : column.headerValue.id,
            ...totals,
          };
        }
        if (column?.headerType === 'DATE_RANGE') {
          return {
            category: getReadableDateRange(timeStep, column.headerValue.startOn, column.headerValue.endOn),
            startOn: column?.headerValue?.startOn,
            endOn: column?.headerValue?.endOn,
            ...totals,
          };
        }
        throw new Error(`Expected column type of DATE_RANGE or QCS_ENTITY, got ${column?.headerType}`);
      });
    }
    return null;
  },
);

export const rowsByParentId = createSelector(getReport, (report) =>
  _groupBy(report?.rows, (row) => row.parentId ?? '-1'),
);
